import { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import * as client from "../../../../client";
import { LOB } from "../../../../api/routes";
import { useToast } from "../../../../context-providers/Toast";
import { SafeReportMetadataResponse } from "../../Conversion";
import { useBulkEnrich, useLocalUser, useSafeReports } from "../../data";
import { StatusIcon } from "../StatusIcon";
import { getAllStatuses, readableStatus } from "../util";
import { formatTimestamp } from "./submissionHistoryUtils";
import sharedStyles from "./shared.module.css";
import styles from "./Enrichment.module.css";

type StatusOption = { name: string; code: client.status };

// Third-party sources the bulk enricher can run.
type SourceOption = {
  key: client.BulkEnrichmentRequest["sources"][number];
  label: string;
  disabled: boolean;
};
const SOURCE_OPTIONS: SourceOption[] = [
  { key: "nearmap", label: "Nearmap", disabled: false },
  { key: "hazard_hub", label: "HazardHub", disabled: false },
  { key: "cape", label: "CAPE", disabled: false },
];

// Selecting more than this many total locations warns (but never blocks) —
// large runs consume vendor/LLM quota and take a while.
const LARGE_SELECTION_LOCATIONS = 2000;

const STATUS_OPTIONS: StatusOption[] = getAllStatuses().map((s) => ({
  name: readableStatus(s),
  code: s,
}));

export const Enrichment = () => {
  const user = useLocalUser();
  const lobsForUser = (user?.lobs ?? []) as LOB[];
  const reports = useSafeReports(lobsForUser).data ?? [];
  const bulkEnrich = useBulkEnrich();
  const toast = useToast();

  const [selectedStatuses, setSelectedStatuses] = useState<StatusOption[]>([]);
  const [companyQuery, setCompanyQuery] = useState("");
  const [selected, setSelected] = useState<SafeReportMetadataResponse[]>([]);
  const [nearmap, setNearmap] = useState(true);
  const [hazardHub, setHazardHub] = useState(true);
  const [cape, setCape] = useState(true);
  const [result, setResult] = useState<client.BulkEnrichmentResponse | null>(
    null
  );

  const sources = useMemo<client.BulkEnrichmentRequest["sources"]>(() => {
    const out: client.BulkEnrichmentRequest["sources"] = [];
    if (nearmap) out.push("nearmap");
    if (hazardHub) out.push("hazard_hub");
    if (cape) out.push("cape");
    return out;
  }, [nearmap, hazardHub, cape]);

  const filteredReports = useMemo(() => {
    const query = companyQuery.trim().toLowerCase();
    return reports.filter((r) => {
      if (
        selectedStatuses.length > 0 &&
        !selectedStatuses.some((s) => s.code === r.status)
      ) {
        return false;
      }
      if (
        query &&
        !r.report_metadata.company_info.company_name
          .toLowerCase()
          .includes(query)
      ) {
        return false;
      }
      return true;
    });
  }, [reports, selectedStatuses, companyQuery]);

  const totalLocations = useMemo(
    () =>
      selected.reduce(
        (sum, r) =>
          sum + (r.report_metadata.precomputed_metadata.num_locations ?? 0),
        0
      ),
    [selected]
  );

  const run = () => {
    setResult(null);
    bulkEnrich.mutate(
      { reportIds: selected.map((r) => r.report_id), sources },
      {
        onSuccess: (resp) => {
          setResult(resp);
          toast.show({
            severity: "success",
            summary: "Enrichment enqueued",
            detail: `Enqueued ${resp.enqueued_count} submission(s)${
              resp.skipped_count ? `, skipped ${resp.skipped_count}` : ""
            }. Track them in the Jobs in Queue tab.`,
          });
        },
        onError: () =>
          toast.show({
            severity: "error",
            summary: "Enrichment failed",
            detail: "There was an error enqueuing the enrichment jobs.",
          }),
      }
    );
  };

  const skipped = result?.results.filter((r) => r.skipped_reason) ?? [];

  return (
    <div className={sharedStyles.page}>
      <Card title="Enrichment">
        <div className={styles.scrollArea}>
          <div className={styles.toolbar}>
            <MultiSelect
              value={selectedStatuses}
              onChange={(e) => setSelectedStatuses(e.value)}
              options={STATUS_OPTIONS}
              optionLabel="name"
              itemTemplate={(option: StatusOption) => (
                <StatusIcon status={option.code} />
              )}
              placeholder="Filter by status"
              maxSelectedLabels={3}
              showClear
              scrollHeight="500px"
            />
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                value={companyQuery}
                onChange={(e) => setCompanyQuery(e.target.value)}
                placeholder="Search company"
              />
            </span>
            <div className={styles.spacer} />
            <span className={styles.selectedCount}>
              {selected.length} selected
            </span>
            <Button
              label="Clear"
              text
              severity="secondary"
              disabled={selected.length === 0}
              onClick={() => setSelected([])}
            />
          </div>

          <DataTable
            value={filteredReports}
            dataKey="report_id"
            selection={selected}
            onSelectionChange={(e) =>
              setSelected(e.value as SafeReportMetadataResponse[])
            }
            selectionMode="multiple"
            paginator
            rows={25}
            rowsPerPageOptions={[25, 50, 100]}
            size="small"
            emptyMessage="No submissions match the current filters."
          >
            <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
            <Column
              header="Company"
              sortable
              sortField="report_metadata.company_info.company_name"
              body={(r: SafeReportMetadataResponse) =>
                r.report_metadata.company_info.company_name
              }
            />
            <Column
              header="Status"
              sortable
              sortField="status"
              body={(r: SafeReportMetadataResponse) => (
                <StatusIcon status={r.status} />
              )}
            />
            <Column
              header="Locations"
              sortable
              sortField="report_metadata.precomputed_metadata.num_locations"
              align="right"
              body={(r: SafeReportMetadataResponse) =>
                (
                  r.report_metadata.precomputed_metadata.num_locations ?? 0
                ).toLocaleString()
              }
            />
            <Column field="lob" header="LOB" sortable />
            <Column
              header="Created"
              sortable
              sortField="created_at_ts"
              body={(r: SafeReportMetadataResponse) =>
                formatTimestamp(r.created_at_ts)
              }
            />
          </DataTable>

          <div className={styles.sourcesLabel}>Sources</div>
          <div className={styles.sources}>
            {SOURCE_OPTIONS.map((source) => {
              const checked =
                source.key === "nearmap"
                  ? nearmap
                  : source.key === "hazard_hub"
                    ? hazardHub
                    : cape;
              const onChange = (value: boolean) => {
                if (source.key === "nearmap") setNearmap(value);
                else if (source.key === "hazard_hub") setHazardHub(value);
                else if (source.key === "cape") setCape(value);
              };
              return (
                <div key={source.key} className={styles.sourceItem}>
                  <Checkbox
                    inputId={`source-${source.key}`}
                    checked={checked}
                    disabled={source.disabled}
                    onChange={(e) => onChange(!!e.checked)}
                  />
                  <label htmlFor={`source-${source.key}`}>{source.label}</label>
                </div>
              );
            })}
          </div>
          <small className={styles.sourcesHint}>
            CAPE bills per property — untick it on large runs to avoid the cost.
          </small>

          <div className={styles.footer}>
            {totalLocations > LARGE_SELECTION_LOCATIONS && (
              <Message
                severity="warn"
                text={`${totalLocations.toLocaleString()} locations across ${selected.length} submissions selected — this is a large run and may take a while and use vendor/LLM quota.`}
              />
            )}
            <div className={styles.runRow}>
              <Button
                label="Run enrichment"
                icon="pi pi-sparkles"
                loading={bulkEnrich.isPending}
                disabled={selected.length === 0 || sources.length === 0}
                onClick={run}
              />
            </div>
            {result && (
              <Message
                severity={skipped.length ? "warn" : "success"}
                text={`Enqueued ${result.enqueued_count} submission(s)${
                  result.skipped_count
                    ? `; skipped ${result.skipped_count}`
                    : ""
                }. Track progress in the Jobs in Queue tab.`}
              />
            )}
            {skipped.length > 0 && (
              <ul className={styles.skippedList}>
                {skipped.map((r) => (
                  <li key={r.report_id}>
                    {r.company_name ?? r.report_id} — {r.skipped_reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
